import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { IMAGES } from "../lib/tokens";
import { API, setSession } from "../lib/api";
import { useWorld } from "../state/World";
import Plate from "../components/Plate";

/**
 * /claim — Reverse-the-Arrow onboarding from Skool.
 *
 * A Skool member clicks a link in the Endeavorum welcome post and lands here
 * on the .org origin. This page is deliberately its own front door — NOT the
 * Threshold — because the ceremony is different: they aren't a stranger,
 * they're a neighbour walking over from the front porch next door. The copy
 * treats them accordingly.
 *
 * Uses plain fetch (not the axios instance) because Claim runs BEFORE any
 * session exists — the shared axios interceptor path adds complexity we do
 * not need for this one-shot mint. On success we store the session token
 * and hard-navigate to /commons, letting WorldProvider boot cleanly on the
 * new session with no React state timing to reason about.
 */
export const Claim = () => {
  const { me } = useWorld();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // Returning-visitor auto-forward. If WorldProvider already lifted a session
  // for this browser (localStorage token was still valid), don't force them
  // to re-claim — walk them straight onto the patio. This runs once when
  // `me` first becomes truthy. Uses a hard nav so the Commons page loads
  // fresh with the full plate + welcome flow instead of a partial re-render.
  useEffect(() => {
    if (me?.user?.id) {
      window.location.assign("/commons");
    }
  }, [me?.user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      return toast.error("An address the Commons can reach.");
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/enter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          display_name: name.trim() || undefined,
          // The Commons origin (.org) will already have portalRef=commons in
          // WorldProvider once we land on /commons. Passing "commons" here
          // records the User's origin_portal correctly no matter which origin
          // the browser was on when they claimed.
          portal: "commons",
        }),
      });
      if (!res.ok) {
        // Pydantic validation errors come back as a list of objects on `detail`.
        // Rendering that object directly would crash React. Extract a
        // readable string whether it's a plain message or a validation-error
        // array.
        let msg = "The Commons did not open. Try again.";
        try {
          const body = await res.json();
          const detail = body?.detail;
          if (Array.isArray(detail)) {
            msg = detail[0]?.msg || "That address didn't fit — try another.";
          } else if (typeof detail === "string") {
            msg = detail;
          }
        } catch (parseErr) {
          // Response body wasn't JSON; keep the default message.
          console.debug("[Claim] non-JSON error body:", parseErr);
        }
        toast.error(msg);
        setBusy(false);
        return;
      }
      const data = await res.json();
      setSession(data.token);
      // Hard nav lets WorldProvider bootstrap from scratch on the Commons
      // origin with the freshly-minted session in localStorage. No race with
      // React re-render timing.
      window.location.assign("/commons");
    } catch (err) {
      toast.error("The Commons did not open. Try again.");
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="claim-page">
      <Plate src={IMAGES.commonsPad} depth={10} />

      {/* Vignette so the arrival copy stays legible over the tavern plate */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-64"
        style={{
          background:
            "linear-gradient(180deg, rgba(11,7,4,0.92) 0%, rgba(11,7,4,0.7) 55%, rgba(11,7,4,0) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40"
        style={{
          background:
            "linear-gradient(0deg, rgba(11,7,4,0.9) 0%, rgba(11,7,4,0) 100%)",
        }}
      />

      {/* Header copy — carries the Skool→Commons continuity */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-col items-center gap-2 px-6 pt-10 md:pt-14"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 0.15 }}
        data-testid="claim-header"
      >
        <p className="mono text-[10px] uppercase tracking-[0.42em] text-[#f2c877] md:text-[11px]">
          The Commons · Reverse-the-Arrow
        </p>
        <h1 className="serif max-w-[720px] text-center text-[clamp(1.4rem,3vw,2.4rem)] font-light leading-tight text-[#fdf3d8]">
          You walked in from{" "}
          <span className="italic text-[var(--atlas-gold)]">the front porch.</span>
        </h1>
        <p className="serif mt-1 max-w-[560px] text-center text-[13px] italic leading-relaxed text-[#e2cba2]/85 md:text-[14px]">
          Skool is where you found us. This is where the doing gets real.
        </p>
      </motion.div>

      {/* The claim card — same glass language as Threshold, tuned for arrival */}
      <div className="relative z-20 flex min-h-screen items-center justify-center px-6 py-32 md:py-40">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.4 }}
          className="w-full max-w-lg"
        >
          <div className="glass-warm rounded-2xl p-6 sm:p-7" data-testid="claim-card">
            <img
              src={IMAGES.logoDark}
              alt="Endeavorum"
              data-testid="claim-brand-lockup"
              className="mb-4 block w-[150px] sm:w-[190px]"
              style={{ mixBlendMode: "screen" }}
            />
            <h2 className="serif mb-3 text-2xl font-light leading-[1.08] text-[#f4ecdd] sm:text-3xl">
              Claim your{" "}
              <span className="italic text-[var(--atlas-gold)]">seat by the fire.</span>
            </h2>
            <p className="mb-5 text-sm leading-relaxed text-white/60">
              Your Pouch starts warm — 25 AXV, yours to spend on whatever
              illuminates you. Nothing hidden. No password in V1. Just an
              address the Commons can reach.
            </p>

            <form className="space-y-3" onSubmit={submit}>
              <input
                className="field"
                data-testid="claim-email-input"
                placeholder="you@somewhere.com"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                className="field"
                data-testid="claim-name-input"
                placeholder="What should MelzAI call you?"
                autoComplete="nickname"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button
                type="submit"
                data-testid="claim-submit-button"
                disabled={busy}
                className="btn btn-gold flex w-full items-center justify-center gap-2"
              >
                {busy ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  <>
                    Step onto the Pad <ArrowRight size={13} />
                  </>
                )}
              </button>
            </form>

            <p className="mono mt-5 text-[9px] uppercase tracking-[0.28em] leading-relaxed text-white/35">
              First arrival = 25 AXV minted to your Pouch · returning
              addresses lift back into their existing Pouch, nothing lost
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Claim;
